import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

// Production configuration
const PUBLIC_ROUTE_CONFIG = {
  REDIRECT_DELAY: 1000,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
  MAX_REDIRECT_ATTEMPTS: 3
};

const PublicRoute = ({ 
  children, 
  redirectIfAuthenticated = true,
  redirectPath = '/feed',
  showRedirectMessage = true,
  allowAuthenticated = false
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const [redirectAttempts, setRedirectAttempts] = useState(0);

  // Handle redirect for authenticated users
  useEffect(() => {
    if (!redirectIfAuthenticated || !isAuthenticated || allowAuthenticated) return;

    if (redirectAttempts >= PUBLIC_ROUTE_CONFIG.MAX_REDIRECT_ATTEMPTS) {
      console.error('Max redirect attempts reached');
      return;
    }

    const timer = setTimeout(() => {
      setRedirecting(true);
      setRedirectAttempts(prev => prev + 1);
    }, PUBLIC_ROUTE_CONFIG.REDIRECT_DELAY);

    return () => clearTimeout(timer);
  }, [isAuthenticated, redirectIfAuthenticated, allowAuthenticated, redirectAttempts]);

  // Loading state
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Redirect authenticated users
  if (isAuthenticated && redirectIfAuthenticated && !allowAuthenticated) {
    if (redirecting || redirectAttempts > 0) {
      return (
        <Navigate 
          to={location.state?.from || redirectPath} 
          replace 
        />
      );
    }

    if (showRedirectMessage) {
      return (
        <Box p={3}>
          <Alert severity="info">
            You are already signed in. Redirecting to your dashboard...
          </Alert>
        </Box>
      );
    }
  }

  // Show public content
  return children;
};

export default PublicRoute;